import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Box, Paper, CircularProgress, Button, Grid } from '@mui/material'
import { Text2 } from 'ui/shorthands/Texts'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: 'auto',
    width: 320,
    maxWidth: '100%',
    paddingTop: theme.spacing(0.5),
  },
  items: {
    padding: theme.spacing(2),
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  sendingModalRoot: {
    width: 320,
  },
  right: {
    display: 'flex',
    placeContent: 'flex-end',
    paddingTop: 0,
    paddingBottom: theme.spacing(1),
  },
  title: {
    color: theme.palette.text.primary,
  },
  titleWithoutChildren: {
    paddingBottom: 0,
    minHeight: 53,
  },
  closeButton: {
    minWidth: 48,
  },
  textButton: {
    minWidth: 48,
    fontWeight: 500,
  },
}))

export type ConfirmContentProps = {
  /**
   * タイトル
   */
  title?: React.ReactNode
  /**
   * OKボタンのラベル
   */
  submitLabel?: string
  /**
   * OKボタンのvariant
   */
  submitVariant?: 'text' | 'outlined' | 'contained'
  /**
   * OKボタンの無効化
   */
  submitDisabled?: boolean
  /**
   * キャンセルボタンのラベル
   */
  cancelLabel?: string
  /**
   * OKの時の関数
   */
  onSubmit?: () => void
  /**
   * キャンセル時の関数
   */
  onCancel?: () => void
  /**
   * 中央に表示されるコンテンツ
   */
  children?: any
  /**
   * クラス名
   */
  className?: string
  /**
   * 送信中
   */
  sending?: boolean
}

export const ConfirmContent: React.FC<ConfirmContentProps> = ({
  title,
  submitLabel,
  submitVariant,
  submitDisabled,
  cancelLabel,
  onSubmit,
  onCancel,
  className,
  children,
  sending,
}: ConfirmContentProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  return (
    <Paper>
      <Grid className={cx(classes.root, className)}>
        {title && (
          <Grid
            className={cx(
              classes.items,
              !children && classes.titleWithoutChildren
            )}
          >
            <Text2 className={classes.title}>{title}</Text2>
          </Grid>
        )}
        {children}
        <Grid className={classes.items}>
          <Box textAlign="right">
            {onCancel && (
              <Button
                className={cx(
                  submitVariant !== 'contained' && classes.textButton
                )}
                color="primary"
                onClick={(e) => {
                  onCancel()
                  e.preventDefault()
                  e.stopPropagation()
                }}
                disabled={sending}
              >
                {cancelLabel || t('キャンセル')}
              </Button>
            )}
            {onSubmit && (
              <Button
                color="primary"
                className={cx(
                  submitVariant !== 'contained' && classes.textButton
                )}
                variant={submitVariant}
                onClick={(e) => {
                  onSubmit()
                  e.preventDefault()
                  e.stopPropagation()
                }}
                disabled={submitDisabled || sending}
              >
                {submitLabel || t('OK')}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  )
}

export type SendingContentError = 'network' | 'timeout' | 'server' | null

export const SendingContent: React.FC<{
  message?: React.ReactNode
  error?: SendingContentError
  onClose?: () => void
}> = ({ message, error, onClose }) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const errors = {
    network: t(
      'errors.ネットワークに繋がっていません。通信環境を確認してください。'
    ),
    timeout: t('errors.通信がタイムアウトしました。もう一度送信してください。'),
    server: t('errors.システムエラーが発生したため予約を完了できません。'),
  }
  const sendingMessage = message || t('送信中です。しばらくお待ちください。')
  return (
    <Paper className={cx(classes.sendingModalRoot, classes.center)}>
      <Grid className={classes.root}>
        {!error && (
          <Grid className={cx(classes.items, classes.center)}>
            <CircularProgress />
          </Grid>
        )}
        <Grid className={cx(classes.items, classes.center)}>
          {error ? errors[error] : sendingMessage}
        </Grid>

        {error && (
          <Grid className={cx(classes.items, classes.right)}>
            <Button
              color="primary"
              className={classes.closeButton}
              onClick={onClose}
            >
              {t('閉じる')}
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}
